'use client';

import { useSession } from '@/lib/auth-client';
import type { User } from '@/lib/auth';
import Link from 'next/link';
import { Button } from '../UIComponents/Button';
import { SidebarTrigger } from '../UIComponents/Sidebar';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent
} from '../UIComponents/DropdownMenu';
import { User as UserIcon, LogOut, KeyRound, Volume2, VolumeX, Sun, Monitor, Moon, Check } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useAtom } from 'jotai';
import { useLogout } from '@/_state/hooks';
import { soundEnabledAtom } from '@/_state/atoms';
import { Switch } from '../UIComponents/Switch';
import ConversationNavbarTitle from './ConversationNavbarTitle.client';
import ConversationNavbarMenu from './ConversationNavbarMenu.client';

export default function AppNavbar({ user }: { user: User | null }) {
	const { data: session } = useSession();
	const { theme, setTheme } = useTheme();
	const logout = useLogout();
	const [soundEnabled, setSoundEnabled] = useAtom(soundEnabledAtom);

	// Client session (reactive — handles logout), with server-provided user as SSR fallback
	const currentUser = session?.user ?? user;

	return (
		<nav className='bg-background/60 sticky top-0 z-10 flex h-15 shrink-0 items-center gap-2 backdrop-blur-md transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12'>
			<div className='flex w-full items-center gap-2 px-2 sm:px-4'>
				{/* Left section - Menu Icon + Conversation title */}
				<div className='flex min-w-0 flex-1 items-center gap-2'>
					<SidebarTrigger className='-ml-1 shrink-0' />
					<ConversationNavbarTitle />
				</div>

				{/* Right section - Conversation actions + Auth */}
				<div className='flex shrink-0 items-center gap-1 sm:gap-2'>
					<ConversationNavbarMenu />
					{currentUser ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant='ghost'
									size='sm'
									className='flex items-center gap-2'
								>
									<UserIcon className='h-4 w-4' />
									<span className='hidden sm:inline'>{currentUser.name || currentUser.email}</span>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align='end'>
								<div className='px-2 py-1.5 text-sm'>
									<p className='text-muted-foreground text-xs'>Signed in as</p>
									<p className='max-w-[200px] truncate font-medium'>{currentUser.email}</p>
								</div>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									role='menuitemcheckbox'
									aria-checked={soundEnabled}
									className='cursor-pointer'
									onSelect={(e) => {
										e.preventDefault();
										setSoundEnabled((prev) => !prev);
									}}
								>
									{soundEnabled ? (
										<Volume2 className='mr-2 h-4 w-4' />
									) : (
										<VolumeX className='text-muted-foreground mr-2 h-4 w-4' />
									)}
									Notify when done
									<Switch
										className='pointer-events-none ml-auto'
										checked={soundEnabled}
										tabIndex={-1}
										aria-hidden
									/>
								</DropdownMenuItem>
								<DropdownMenuSub>
									<DropdownMenuSubTrigger className='cursor-pointer'>
										{theme === 'light' ? (
											<Sun className='mr-2 h-4 w-4' />
										) : theme === 'dark' ? (
											<Moon className='mr-2 h-4 w-4' />
										) : (
											<Monitor className='mr-2 h-4 w-4' />
										)}
										Theme
									</DropdownMenuSubTrigger>
									<DropdownMenuSubContent>
										<DropdownMenuItem
											onClick={() => setTheme('light')}
											className='cursor-pointer'
										>
											<Sun className='mr-2 h-4 w-4' />
											Light
											{theme === 'light' && <Check className='ml-auto h-4 w-4' />}
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => setTheme('system')}
											className='cursor-pointer'
										>
											<Monitor className='mr-2 h-4 w-4' />
											System
											{theme === 'system' && <Check className='ml-auto h-4 w-4' />}
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => setTheme('dark')}
											className='cursor-pointer'
										>
											<Moon className='mr-2 h-4 w-4' />
											Dark
											{theme === 'dark' && <Check className='ml-auto h-4 w-4' />}
										</DropdownMenuItem>
									</DropdownMenuSubContent>
								</DropdownMenuSub>
								<DropdownMenuItem asChild>
									<Link
										href='/forgot-password'
										className='cursor-pointer'
									>
										<KeyRound className='mr-2 h-4 w-4' />
										Reset Password
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={logout}
									className='text-destructive focus:text-destructive cursor-pointer'
								>
									<LogOut className='mr-2 h-4 w-4' />
									Sign out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<>
							<Link href='/login'>
								<Button
									size='sm'
									className='text-xs sm:text-sm md:px-4 md:py-2 md:text-base'
								>
									Login
								</Button>
							</Link>
							<Link href='/signup'>
								<Button
									variant='outline'
									size='sm'
									className='text-xs sm:text-sm md:px-4 md:py-2 md:text-base'
								>
									Sign Up
								</Button>
							</Link>
						</>
					)}
				</div>
			</div>
		</nav>
	);
}
